<template>
  <div class="RecordRespondents" v-if="selectedStudy && currentTab">
		<div class="d-sr-only-focusable float-right text-decoration-underline" tabindex="0" @keyup.enter="focusToTop" @click="focusToTop" ref="firstFocusable" role="link">Go to top of page</div>
    <h2 class="left-align">Track Respondents</h2>
    <v-row no-gutters>
      <v-col>
        <p class="p3 left-align"><label>Filter by username</label></p>
      </v-col>
      <v-col></v-col>
    </v-row>
    <v-row no-gutters>
      <v-col>
        <v-text-field
          outlined
					placeholder="Username"
					single-line
					hide-details
					v-model="filterUserNameValue"
					:aria-label="'Filter by username: ' + filterUserNameValue"
				></v-text-field>
      </v-col>
	  <v-col>
		<v-btn @click="runSearch()" class="btn-default btn-search" text>Search</v-btn>
	  </v-col>
      <v-col>
        <v-btn @click="clearFilters()" class="btn-default" text>Clear Filter</v-btn>
      </v-col>
    </v-row>
		<p v-if="selectedStudy.recordStudyType" class="track-extra-info left-align">For consecutive records to show up in the Completed Records column, every single day within the consecutive record needs to be complete. Otherwise, the record will show up in the Quit Records column. The status of individual record days is shown on the Track Records tab.</p>
    <br v-if="selectedStudy.recordStudyType">
    <v-btn class="btn-link text-none" text @click="selectAll()">Select All</v-btn>
    <v-btn class="btn-link text-none" text @click="deselectAll()">Deselect All</v-btn>
		<vue-json-to-csv
      :json-data="selected"
      :csv-title="'selected-respondents'"
    >
      <v-btn class="btn-link text-none" text>Export Selected</v-btn>
    </vue-json-to-csv>
    <vue-json-to-csv
      :json-data="respondentsResult.respondents"
      :csv-title="'all-respondents'"
    >
      <v-btn class="btn-link text-none" text>Export All</v-btn>
    </vue-json-to-csv>
    <div v-show="headers.length !== 0" class="track-respondents-table-container">
			<v-data-table
				v-model="selected"
				:headers="headers"
				:items="respondentsResult.respondents"
				:loading="isLoading"
				item-key="userName"
				show-select
				class="elevation-1"
				:server-items-length="respondentsResult.total"
				:options.sync="dataTableOptions"
				@update:options="onUpdateOptions"
				:page.sync="dataTableOptions.page"
				hide-default-footer
			>
				<template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
					<v-tooltip :disabled="h.hover === ''" :key="h.value" bottom color="#333">
						<template v-slot:activator="{ on }">
							<span v-on="on" class="columnTitleText" tabIndex="0" v-on:keyup.enter="(e) => e.target.click()">{{h.text}}</span>
						</template>
						<span>{{h.hover}}</span>
					</v-tooltip>
				</template>
				<!-- for the check box header -->
				<template v-slot:header.data-table-select="{ value, selected }">
					<v-checkbox
            :value="value"
            :selected="selected"
						aria-label="select all"
            hide-details
            class="mt-0"
						@change="checkboxHeader"
					>
					</v-checkbox>
				</template>
				<!-- for the check boxes -->
				<template v-slot:item.data-table-select="{ item, isSelected }">
					<v-checkbox
						:value="isSelected"
            :isSelected="item"
						aria-label="respondent"
            hide-details
            class="mt-0"
						@change="checkboxData(item)"
						>
					</v-checkbox>
				</template>
			</v-data-table>
			<div class="pagination-container">
				<v-row>
					<v-col :cols="2">
						<v-text-field
							:value="dataTableOptions.itemsPerPage"
							label="Items per page"
							type="number"
							:min="minItemsPerPage"
							:max="maxItemsPerPage"
							@input="onItemsPerPageTextInput"
						></v-text-field>
					</v-col>
					<v-col :cols="8">
						<v-pagination
							v-model="dataTableOptions.page"
							:length="pageCount"
							:total-visible="7"
						></v-pagination>
					</v-col>
					<v-col :cols="2">
						<v-text-field
							:value="footerTotalText"
							disabled
							variant="plain"
						></v-text-field>
					</v-col>
				</v-row>
			</div>
		</div>
  </div>
</template>

<script>
import { analysisApiService } from '@/services/analysisApiService.js';
import VueJsonToCsv from 'vue-json-to-csv';
import { utilityService } from '@/services/utilityService.js';
export default {
	name: 'RecordRespondents',
	components: { VueJsonToCsv },
	props: {
	},
	data: () => ({
		selected: [],
		respondentsResult: {
			respondents: [],
			total: 0
		},
		page: 1,
		itemsPerPage: 15,
		filterUserNameValue: '',
		studyUuid: '',
		isLoading: false,
		minItemsPerPage: 1,
		maxItemsPerPage: 25,
		dataTableOptions: {
			sortBy: ['userName'],
			sortDesc: [false],
			page: 1,
			itemsPerPage: 15
		}
	}),
	computed: {
		headers: function () {
			return [
				{ text: 'Username', value: 'userName', hover: '', class: 'data-table-header', filter: this.filterUsername, align: 'center' },
				{ text: 'Quit Records', value: 'recallsQuitCount', hover: '', class: 'data-table-header', align: 'center', sortable: false },
				{ text: 'Completed Records', value: 'recallsCompleteCount', hover: '', class: 'data-table-header', align: 'center', sortable: false },
				{ text: 'Records Remaining', value: 'recallsRemainingCount', hover: '', class: 'data-table-header', align: 'center', sortable: false }
			]
		},
		selectedStudy: function () {
			// If the selectedStudy changes, the radio buttons and checkbox properties need to be updated.
			if (this.studyUuid !== this.$store.getters.getSelectedStudy.studyUuid) {
				this.updateSelected();
			}
			return this.$store.getters.getSelectedStudy;
		},
		currentTab: function () {
			const tabPosition = {
				tabIndex: parseInt(this.$store.getters.getCurrentStudyTabIndex),
				subTabIndex: parseInt(this.$store.getters.getCurrentStudySubTabIndex)
			};

			this.checkTabMatch(tabPosition);

			return tabPosition;
		},
		pageCount: function () {
			if (this.dataTableOptions) {
				return this.calculatePageCount(this.dataTableOptions.itemsPerPage);
			}
			return 0;
		},
		footerTotalText: function () {
			if (this.respondentsResult.respondents.length) {
				var startPos = (this.dataTableOptions.page - 1) * this.dataTableOptions.itemsPerPage;
				startPos++;
				var endPos;

				if (this.dataTableOptions.page == this.pageCount) {
					endPos = this.respondentsResult.total;
				} else {
					endPos = this.dataTableOptions.page * this.dataTableOptions.itemsPerPage;
				}

				var total = this.respondentsResult.total;
				return startPos + '-' + endPos + " of " + total;
			} else {
				return '0';
			}
		}
	},
	methods: {
		checkboxHeader (e) {
			if (e) {
				this.selectAll()
			} else {
				this.deselectAll()
			}
		},
		checkboxData (obj) {
			if (!this.selected.includes(obj)) {
				this.selected.push(obj)
			} else {
				for (var i = this.selected.length; i--;) {
					if (this.selected[i] === obj) this.selected.splice(i, 1);
				}
			}
		},
		updateSelected () {
			this.studyUuid = this.$store.getters.getSelectedStudy.studyUuid;
			this.loadData();
		},
		async loadData () {
			const me = this;
			this.isLoading = true;

			const page = this.dataTableOptions.page;
			const pageSize = this.dataTableOptions.itemsPerPage;
			const sortDir = this.dataTableOptions.sortDesc[0] ? 'DESC' : 'ASC';

			await analysisApiService.loadTrackRespondents(this.selectedStudy.studyId, page, pageSize, sortDir, this.filterUserNameValue)
				.then(async function (response) {
					if (response && response.data) {
						me.respondentsResult.respondents = response.data.trackRespondentsList;
						me.respondentsResult.total = response.data.totalResults;
					}
					me.isLoading = false;
				}).catch(function (err) {
					me.isLoading = false;
					utilityService.showErrorMessage(err);
					me.respondentsResult.total = 0;
				});
		},
		async checkTabMatch ({ tabIndex, subTabIndex } = {}) {
			var tabsPos = await utilityService.getTabsPositionByName('Track Respondents', this.$studyTabConfig);
			if (tabsPos.tabPos === tabIndex && tabsPos.subtabPos === subTabIndex) {
				this.loadData();
			}
		},
		filterUsername (val) {
			if (!this.filterUserNameValue) {
				return true;
			}

			return val.toLowerCase().includes(this.filterUserNameValue.toLowerCase());
		},
		onUpdateOptions: function () {
			this.loadData();
		},
		runSearch () {
			// When running a new search, reset the page to 1
			this.dataTableOptions.page = 1;
			this.loadData();
		},
		clearFilters () {
			this.filterUserNameValue = '';
			this.dataTableOptions.page = 1;
			this.loadData();
		},
		selectAll () {
			this.selected = this.respondentsResult.respondents;
		},
		deselectAll () {
			this.selected = [];
		},
		/**
		 * Handles the manual typing of the page size event
		 * @param {*} event - The raised event that holds the entered page size
		 */
		onItemsPerPageTextInput: function (event) {
			let parsedItemsPerPage = parseInt(event, 10);
			if (!isNaN(parsedItemsPerPage)) {
				// If the entered page size is greater than the total number of recalls, set the page size to the total number of recalls
				if (parsedItemsPerPage > this.maxItemsPerPage) {
					parsedItemsPerPage = this.maxItemsPerPage;
				}

				// If the entered page size is less than the minimum page size, set the page size to the minimum page size
				if (parsedItemsPerPage < this.minItemsPerPage) {
					parsedItemsPerPage = this.minItemsPerPage;
				}

				// If the items per page is increasing there's a possibility the selected page is out of range
				if (parsedItemsPerPage > this.dataTableOptions.itemsPerPage) {
					// if it is out of range shift the value to the last page of the new count
					const newPageCount = this.calculatePageCount(parsedItemsPerPage);
					if (this.dataTableOptions.page > newPageCount) {
						this.dataTableOptions.page = newPageCount;
					}
				}

				this.dataTableOptions.itemsPerPage = parsedItemsPerPage;
			}
		},
		focusToTop () {
			var myHeader = document.getElementById('skipToMain')
			myHeader.focus()
		},
		calculatePageCount (itemsPerPage) {
			const countRecalls = this.respondentsResult.total ? this.respondentsResult.total : 0;
			return Math.ceil(countRecalls / itemsPerPage);
		}
	}
}
</script>

<style>
</style>
